// /markers/$markerId/edit — the Authoring-archetype revise/automate route
// for markers. `GET /v1/markers/{id}` seeds the form, `?mode=` selects
// revise vs automate (validated by the route's `parseMarkerEditSearch`), and
// success/cancel return to /markers preserving only the `agent` param.

import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";

import type { components } from "../api/schema";
import {
  parseMarkerEditSearch,
  type MarkerEditSearch,
} from "../lib/routerSearch";
import type { WorkingScopeSearch } from "../lib/workingScope";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import { markerReferenceWarningDescription } from "../lib/markerReferenceWarnings";
import {
  InlineError,
  Page,
  PageHeader,
  Button,
  Skeleton,
  Stack,
  Text,
  useToast,
} from "../ui";
import {
  MarkerAuthoringForm,
  type JudgeModelPolicy,
  type MarkerAuthoringSubmission,
} from "./MarkerAuthoringForm";
import { MarkerAuthoringLeaveGuard } from "./markerAuthoringLeaveGuard";

type MarkerResponse = components["schemas"]["MarkerResponse"];
type MarkerEditMode = "automate" | "revise";

// Fail-closed policy while /v1/me is pending or unavailable: the judge model
// select renders empty rather than allowing free text.
const unavailableJudgeModels: JudgeModelPolicy = {
  allows_free_text: false,
  model_refs: [],
  model_options: [],
};

async function markerEditMutationRequest(
  submission: MarkerAuthoringSubmission,
): Promise<MarkerResponse> {
  if (submission.mode === "revise") {
    const { data, error } = await apiClient.POST("/v1/markers/{id}:revise", {
      body: submission.body,
      params: { path: { id: submission.markerId } },
    });
    if (error) {
      throw new ApiRequestError(error);
    }
    return data;
  }
  if (submission.mode === "automate") {
    const { data, error } = await apiClient.POST("/v1/markers/{id}:automate", {
      body: submission.body,
      params: { path: { id: submission.markerId } },
    });
    if (error) {
      throw new ApiRequestError(error);
    }
    return data;
  }
  throw new Error("Marker edit only revises or automates.");
}

export function MarkerEdit() {
  const params = useParams({ strict: false });
  const markerId = typeof params.markerId === "string" ? params.markerId : "";
  const rawSearch: WorkingScopeSearch & MarkerEditSearch = useSearch({
    strict: false,
  });
  const search = { ...rawSearch, ...parseMarkerEditSearch({ ...rawSearch }) };
  const mode: MarkerEditMode =
    search.mode === "automate" ? "automate" : "revise";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [isDirty, setIsDirty] = useState(false);

  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/me");
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  const marker = useQuery({
    enabled: markerId.length > 0,
    queryKey: queryKeys.markers.detail(markerId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/markers/{id}", {
        params: { path: { id: markerId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  const saveMarker = useMutation({
    mutationFn: markerEditMutationRequest,
    onSuccess: async (saved, submission) => {
      queryClient.setQueryData(queryKeys.markers.detail(saved.id), saved);
      toast.success(
        submission.mode === "revise"
          ? "Marker revision saved. Existing transcripts were not re-evaluated."
          : "Marker automated",
        { description: markerReferenceWarningDescription(saved) },
      );
      await Promise.all([
        // Prefix match: `list()` invalidates the team-wide AND every
        // agent-filtered markers list.
        queryClient.invalidateQueries({ queryKey: queryKeys.markers.list() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.answers.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.alignment.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.metrics.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.batches.all }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.markers.detail(saved.id),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.markerRevisions.list({ marker_id: saved.id }),
        }),
      ]);
      setIsDirty(false);
      await navigate({
        ignoreBlocker: true,
        search: ({ agent, team }: WorkingScopeSearch) =>
          search.alignment_return
            ? {
                ...search.alignment_return,
                agent,
                team,
                tab: "alignment",
                marker: saved.id,
              }
            : { agent, team },
        to: search.alignment_return ? "/review" : "/markers",
      });
    },
    onError: (error, submission) => {
      toast.error(
        submission.mode === "revise"
          ? "Marker version save failed"
          : "Marker automation failed",
        {
          description: requestErrorDescription(error, "Marker request failed."),
        },
      );
    },
  });

  function cancel(): void {
    void navigate({
      search: ({ agent, team }: WorkingScopeSearch) =>
        search.alignment_return
          ? {
              ...search.alignment_return,
              agent,
              team,
              tab: "alignment",
              evaluate: undefined,
            }
          : { agent, team },
      to: search.alignment_return ? "/review" : "/markers",
    });
  }

  return (
    <Page variant="workspace">
      <MarkerAuthoringLeaveGuard isDirty={isDirty} />
      <MarkerEditBody
        agentId={search.agent}
        errorMessage={marker.isError ? marker.error.message : undefined}
        isPending={marker.isPending}
        judgeModelPolicy={me.data?.judge_model_policy ?? unavailableJudgeModels}
        marker={marker.data}
        mode={mode}
        onCancel={cancel}
        onDirtyChange={setIsDirty}
        onSubmit={async (submission) => {
          await saveMarker.mutateAsync(submission);
        }}
      />
    </Page>
  );
}

interface MarkerEditBodyProps {
  agentId?: string;
  errorMessage?: string;
  isPending: boolean;
  judgeModelPolicy: JudgeModelPolicy;
  marker?: MarkerResponse;
  mode: MarkerEditMode;
  onCancel: () => void;
  onDirtyChange: (isDirty: boolean) => void;
  onSubmit: (submission: MarkerAuthoringSubmission) => Promise<void>;
}

function MarkerEditBody({
  agentId,
  errorMessage,
  isPending,
  judgeModelPolicy,
  marker,
  mode,
  onCancel,
  onDirtyChange,
  onSubmit,
}: MarkerEditBodyProps) {
  if (isPending) {
    // Structure known (the form region), so a skeleton — never a blank page.
    return <Skeleton label="Loading marker" rows={5} shape="table-row" />;
  }
  if (errorMessage !== undefined) {
    return <InlineError>{errorMessage}</InlineError>;
  }
  if (marker === undefined) {
    return null;
  }
  // Never a silently-wrong form (markers.md States): built-ins cannot be
  // revised, and automate requires a human-only current revision.
  if (marker.builtin_key != null) {
    return (
      <MarkerEditNotice
        onCancel={onCancel}
        message="This marker cannot be revised."
      />
    );
  }
  if (mode === "automate" && marker.current_revision.evaluator != null) {
    return (
      <MarkerEditNotice
        onCancel={onCancel}
        message="This marker cannot be automated."
      />
    );
  }
  return (
    <MarkerAuthoringForm
      agentId={marker.agent_id ?? agentId ?? undefined}
      judgeModelPolicy={judgeModelPolicy}
      marker={marker}
      mode={mode}
      renderHeader={(actions) => <PageHeader actions={actions} compact />}
      onCancel={onCancel}
      onDirtyChange={onDirtyChange}
      onSubmit={onSubmit}
    />
  );
}

function MarkerEditNotice({
  onCancel,
  message,
}: {
  onCancel: () => void;
  message: string;
}) {
  return (
    <Stack gap={3}>
      <Text>{message}</Text>
      <Button variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
    </Stack>
  );
}
